/** @jsxImportSource @emotion/react */
import * as React from 'react';
import * as R from 'ramda';
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { BLUE_CREEK, COOLER_GREY, WHITE } from '../../styles/colors';
import { UNSELECTABLE_TEXT, XLargeText } from '../../styles/styles';
import { getResource } from '../../language/resources/getResource';


const LabelWrapper = styled('label')`
  position: relative;
  display: inline-block;
  width: 85px;
  height: 34px;
`;

const CheckboxInput = styled('input')`
  opacity: 0;
  width: 0;
  height: 0;
`;

const ToggleSpan = styled('span')`
  ${UNSELECTABLE_TEXT};
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 34px;
  box-shadow: 2px 6px 18px 0 #00000040;
  transition: .4s;
  ${XLargeText};
  padding-left: 34px;
  pointer-events: none;
  display: flex;
  flex-direction: row;
  align-items: center;

  &:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    border-radius: 50%;
    transition: .4s;
  }

  input:checked + & {
    padding-left: 13px;
    color: ${WHITE};

    &:before {
      transform: translateX(52px);
    }
  }
`;


/**
 * ToggleSwitch
 * This component creates a friendly slider toggle for displaying an enabled/disabled state in a prettier fashion than
 * a standard checkbox.
 *
 * @param isOn - Boolean. Determines whether the toggle is on or off
 * @param onToggle - A function to call when the toggle is clicked
 * @param title - Optional. Provides a title to show on mouseover
 * @param onColor - Optional. The background color to use when value is true. BLUE_CREEK is the default
 * @param offColor - Optional. The background color to use when the value is false. COOLER_GREY is the default
 * @param css - Optional. A css to apply to the top-level element for styling
 */
export default class ToggleSwitch extends React.PureComponent<{
  isOn: boolean;
  onToggle: (value: boolean) => void;
  title?: string;
  disable?: boolean;
  onColor?: string;
  offColor?: string;
  rootCss?: SerializedStyles;
  resources: Record<string, any>
}> {
  private backgroundColors = css`
    background-color: ${R.prop('offColor', this.props) || COOLER_GREY};

    input:checked + & {
      background-color: ${R.prop('onColor', this.props) || BLUE_CREEK};
    }
  `;

  private onChange = (event) => {
    const isOn = event.target.checked;
    this.props.onToggle?.(isOn);
  };

  public render = () => {

    return (
      <LabelWrapper css={this.props.rootCss} title={this.props.title}>
        <CheckboxInput type='checkbox'
                       checked={this.props.isOn}
                       disabled={!!this.props.disable}
                       onChange={this.onChange}/>
        <ToggleSpan css={this.backgroundColors}>{this.props.isOn ? getResource(this.props.resources, 'TOGGLE_SWITCH_ON') : getResource(this.props.resources, 'TOGGLE_SWITCH_OFF')}</ToggleSpan>
      </LabelWrapper>
    );
  }
}
